import type {
  DerivedCase,
  DerivedValue,
  FactType,
  HttpTool,
  InputType,
  ItemFieldType,
  ListItemField,
  NarrowBy,
  Requirement,
  RequirementType,
  ResponseSave,
  ScalarType,
  ScenarioConfig,
  ScenarioConnection,
  ScenarioFact,
  ScenarioIssue,
  ScenarioTool,
  SuccessRule,
  SystemTool,
  SystemToolMeta,
  SystemToolParamMeta,
  TemporalType,
  ToolInput,
  ToolRequest,
  ValueSource,
  Weekday,
} from './types';
import {
  conditionIncompatible,
  newCondition,
  renameFactInCondition,
  renameToolInCondition,
} from './conditions';

export type Path = (string | number)[];

export const isSystemTool = (tool: ScenarioTool): tool is SystemTool => tool.type === 'system';

export const INPUT_TYPES: InputType[] = ['string', 'integer', 'number', 'boolean', 'date'];
export const TEMPORAL_TYPES: TemporalType[] = ['date', 'time', 'datetime'];
export const SCALAR_TYPES: ScalarType[] = [...INPUT_TYPES, 'time', 'datetime'];
export const ITEM_FIELD_TYPES: ItemFieldType[] = [...SCALAR_TYPES, 'string_list'];
export const DERIVED_TYPES: FactType[] = ['string', 'boolean'];
export const WEEKDAYS: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const isTemporalType = (type: string | undefined): type is TemporalType =>
  TEMPORAL_TYPES.includes(type as TemporalType);

export const isDerivedFact = (fact: ScenarioFact | undefined): boolean => fact?.source === 'derived';

/** Facts with revisions; derived facts are computed and cannot be listed as unchanged. */
export const storedFacts = (config: ScenarioConfig): ScenarioFact[] => config.facts.filter(f => !isDerivedFact(f));

export const omitKeys = <T extends object, K extends keyof T>(target: T, ...keys: K[]): Omit<T, K> => {
  const copy = { ...target };
  for (const key of keys) delete copy[key];
  return copy;
};

export const setIn = <T>(target: T, path: Path, value: unknown): T => {
  if (path.length === 0) return value as T;
  const [head, ...rest] = path;
  if (Array.isArray(target)) {
    const copy = [...target];
    copy[head as number] = setIn(copy[head as number], rest, value);
    return copy as T;
  }
  const source = (target ?? {}) as Record<string, unknown>;
  return { ...source, [head]: setIn(source[head as string], rest, value) } as T;
};

export const removeAt = <T>(items: T[], index: number): T[] => items.filter((_, i) => i !== index);

export const moveItem = <T>(items: T[], from: number, to: number): T[] => {
  if (to < 0 || to >= items.length) return items;
  const copy = [...items];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
};

export const uniqueName = (base: string, existing: string[]): string => {
  if (!existing.includes(base)) return base;
  let index = 2;
  while (existing.includes(`${base}_${index}`)) index += 1;
  return `${base}_${index}`;
};

export const emptyConfig = (): ScenarioConfig => ({
  version: 1,
  name: '',
  description: '',
  connections: [{ id: 'api', base_url: 'https://' }],
  facts: [],
  tools: [],
});

export const newConnection = (config: ScenarioConfig): ScenarioConnection => ({
  id: uniqueName(
    'api',
    config.connections.map(c => c.id)
  ),
  base_url: 'https://',
});

export const newItemField = (existing: ListItemField[]): ListItemField => ({
  name: uniqueName(
    'field',
    existing.map(f => f.name)
  ),
  type: 'string',
});

export const newFact = (config: ScenarioConfig): ScenarioFact => ({
  name: uniqueName(
    'field',
    config.facts.map(f => f.name)
  ),
  type: 'string',
  source: 'api',
  visibility: 'internal',
});

export const newInput = (tool: HttpTool): ToolInput => ({
  name: uniqueName(
    'input',
    tool.inputs.map(i => i.name)
  ),
  type: 'string',
  required: true,
  description: '',
});

export const newTool = (config: ScenarioConfig): HttpTool => ({
  id: uniqueName(
    'new_tool',
    config.tools.map(t => t.id)
  ),
  title: '',
  description: '',
  enabled: true,
  operation: 'read',
  inputs: [],
  requires: [],
  request: {
    connection: config.connections[0]?.id ?? '',
    method: 'GET',
    path: '/',
    path_params: [],
    query: [],
    body: [],
  },
  response: {
    success_status: [200],
    success_rule: { type: 'http_status' },
    save: [],
    failure_hint: '',
  },
});

/** Stored string facts a system tool parameter may be bound to; api_only parameters accept API facts only. */
export const bindableFacts = (config: ScenarioConfig, param: SystemToolParamMeta): ScenarioFact[] =>
  config.facts.filter(f => f.type === 'string' && !isDerivedFact(f) && (!param.api_only || f.source === 'api'));

/** Binds every parameter to the fact of the same name when such a string fact exists (API facts for api_only). */
export const newSystemTool = (config: ScenarioConfig, meta: SystemToolMeta): SystemTool => {
  const bind: Record<string, string> = {};
  for (const param of meta.params) {
    if (param.bindable && bindableFacts(config, param).some(f => f.name === param.name)) {
      bind[param.name] = param.name;
    }
  }
  return {
    type: 'system',
    id: uniqueName(
      meta.id,
      config.tools.map(t => t.id)
    ),
    title: meta.title,
    description: meta.description,
    enabled: true,
    system: meta.id,
    bind,
    apply_corrections: false,
    success_when: meta.success_modes[0] ?? '',
    requires: [],
  };
};

export const newRequirement = (type: RequirementType, previous?: Requirement): Requirement => {
  const message = previous?.message ? { message: previous.message } : {};
  const fact = previous && 'fact' in previous ? previous.fact : '';
  switch (type) {
    case 'condition':
      return { type, when: newCondition('compare'), message: previous?.message ?? '' };
    case 'fact_present':
    case 'fact_non_empty':
      return { type, fact, ...message };
    case 'fact_in':
      return { type, fact, values: [''], ...message };
    case 'system_present':
      return { type, field: 'caller_number', ...message };
    case 'tool_success':
      return { type, tool: '', unchanged: [], ...message };
  }
};

export const newValueSource = (source: ValueSource['source'], previous?: ValueSource): ValueSource => {
  if (source === 'constant') {
    return { source, value: previous?.source === 'constant' ? previous.value : '' };
  }
  return { source, key: previous && previous.source !== 'constant' ? previous.key : '' };
};

export const newSuccessRule = (type: SuccessRule['type'], previous?: SuccessRule): SuccessRule => {
  const pointer = previous && 'pointer' in previous ? previous.pointer : '/';
  if (type === 'http_status') return { type };
  if (type === 'exactly_one') {
    return previous?.type === 'exactly_one' && previous.narrow_by
      ? { type, pointer, narrow_by: previous.narrow_by }
      : { type, pointer };
  }
  return { type, pointer, value: previous?.type === 'field_equals' ? previous.value : '' };
};

/** Inputs that may carry the caller's answer for narrow_by: optional strings (the first call is made without it). */
export const narrowInputs = (tool: HttpTool): ToolInput[] =>
  tool.inputs.filter(input => input.type === 'string' && !input.required);

/** Switches asking the caller on several matches; only `exactly_one` supports it. */
export const withNarrowBy = (tool: HttpTool, enabled: boolean): HttpTool => {
  const rule = tool.response.success_rule;
  if (rule.type !== 'exactly_one') return tool;
  const next = enabled
    ? { ...rule, narrow_by: rule.narrow_by ?? { input: narrowInputs(tool)[0]?.name ?? '', fields: [] } }
    : omitKeys(rule, 'narrow_by');
  return { ...tool, response: { ...tool.response, success_rule: next } };
};

/** An empty flag pointer is omitted: the response then counts as complete. */
export const withNarrowTruncated = (narrowBy: NarrowBy, pointer: string): NarrowBy =>
  pointer ? { ...narrowBy, truncated: pointer } : omitKeys(narrowBy, 'truncated');

export const newResponseSave = (): ResponseSave => ({
  pointer: '/',
  fact: '',
  required: true,
  mode: 'replace',
});

export const withSaveMode = (save: ResponseSave, mode: ResponseSave['mode']): ResponseSave => {
  if (mode === 'append_unique') return { ...save, mode, unique_key: save.unique_key ?? '' };
  return { ...omitKeys(save, 'unique_key'), mode };
};

/** A source format is kept only while the target fact is a date, time or datetime. */
export const withSaveFact = (save: ResponseSave, fact: string, facts: ScenarioFact[]): ResponseSave => {
  const type = facts.find(f => f.name === fact)?.type;
  return isTemporalType(type) ? { ...save, fact } : { ...omitKeys(save, 'format'), fact };
};

/** An empty format means ISO and is omitted. */
export const withSaveFormat = (save: ResponseSave, format: string): ResponseSave =>
  format ? { ...save, format } : omitKeys(save, 'format');

const toDerivedValue = (value: DerivedValue, type: FactType): DerivedValue =>
  type === 'boolean' ? value === true || value === 'true' : String(value);

export const newDerivedCase = (type: FactType): DerivedCase => ({
  when: newCondition('compare'),
  value: type === 'boolean' ? true : '',
});

/** Case values and the default of a derived fact follow its type (string <-> boolean). */
export const withFactType = (fact: ScenarioFact, type: FactType): ScenarioFact => {
  if (type === 'list') {
    return {
      ...fact,
      type,
      max_items: fact.max_items ?? 50,
      item_fields: fact.item_fields?.length ? fact.item_fields : [{ name: 'id', type: 'string' }],
    };
  }
  const scalar = { ...omitKeys(fact, 'max_items', 'item_fields'), type };
  if (!isDerivedFact(fact)) return scalar;
  return {
    ...scalar,
    cases: (fact.cases ?? []).map(item => ({ ...item, value: toDerivedValue(item.value, type) })),
    ...(fact.default === undefined ? {} : { default: toDerivedValue(fact.default, type) }),
  };
};

/** An empty default is omitted: without one the fact stays empty when no case is met. */
export const withDerivedDefault = (fact: ScenarioFact, value: DerivedValue | undefined): ScenarioFact =>
  value === undefined || value === '' ? omitKeys(fact, 'default') : { ...fact, default: value };

export const withOperation = (tool: HttpTool, operation: HttpTool['operation']): HttpTool => {
  if (operation === 'write') {
    return {
      ...tool,
      operation,
      confirmation: tool.confirmation ?? { review_fields: [], question: '' },
    };
  }
  return { ...omitKeys(tool, 'confirmation'), operation };
};

/** An empty value removes the limit; the backend validates the allowed range. */
export const withMaxFailedAttempts = (tool: HttpTool, value: string): HttpTool => {
  const parsed = value.trim() === '' ? undefined : Number(value);
  if (parsed === undefined || !Number.isFinite(parsed)) return omitKeys(tool, 'max_failed_attempts');
  return { ...tool, max_failed_attempts: parsed };
};

export const withItemFieldType = (field: ListItemField, type: ListItemField['type']): ListItemField => {
  if (type === 'integer' || type === 'number')
    return { ...omitKeys(field, 'max_items', 'weekdays'), type };
  if (type === 'string_list')
    return { ...omitKeys(field, 'min', 'max', 'weekdays'), type, max_items: field.max_items ?? 10 };
  if (type === 'date') return { ...omitKeys(field, 'min', 'max', 'max_items'), type };
  return { ...omitKeys(field, 'min', 'max', 'max_items', 'weekdays'), type };
};

/** Days are kept in week order; no selected day removes the limit. */
export const withItemFieldWeekdays = (field: ListItemField, days: string[]): ListItemField => {
  const weekdays = WEEKDAYS.filter(day => days.includes(day));
  return weekdays.length ? { ...field, weekdays } : omitKeys(field, 'weekdays');
};

/** `required` is omitted when true so untouched JSON stays identical. */
export const withItemFieldRequired = (field: ListItemField, required: boolean): ListItemField =>
  required ? omitKeys(field, 'required') : { ...field, required: false };

const PLACEHOLDER_RE = /\{([^{}]*)\}/g;

export const pathPlaceholders = (path: string): string[] => {
  const names: string[] = [];
  for (const match of path.matchAll(PLACEHOLDER_RE)) {
    if (match[1] && !names.includes(match[1])) names.push(match[1]);
  }
  return names;
};

export const withPath = (request: ToolRequest, path: string): ToolRequest => {
  const placeholders = pathPlaceholders(path);
  const existing = new Map(request.path_params.map(p => [p.name, p]));
  return {
    ...request,
    path,
    path_params: placeholders.map(
      name => existing.get(name) ?? { name, value: { source: 'fact', key: name } }
    ),
  };
};

const mapTools = (config: ScenarioConfig, fn: (tool: ScenarioTool) => ScenarioTool): ScenarioConfig => ({
  ...config,
  tools: config.tools.map(fn),
});

const renameInSource = (value: ValueSource, source: ValueSource['source'], from: string, to: string) =>
  value.source === source && value.source !== 'constant' && value.key === from
    ? { ...value, key: to }
    : value;

const renameInRequest = (
  request: ToolRequest,
  source: ValueSource['source'],
  from: string,
  to: string
): ToolRequest => ({
  ...request,
  path_params: request.path_params.map(p => ({ ...p, value: renameInSource(p.value, source, from, to) })),
  query: request.query.map(q => ({ ...q, value: renameInSource(q.value, source, from, to) })),
  body: request.body.map(b => ({ ...b, value: renameInSource(b.value, source, from, to) })),
});

const mapCases = (fact: ScenarioFact, fn: (when: DerivedCase['when']) => DerivedCase['when']): ScenarioFact =>
  fact.cases ? { ...fact, cases: fact.cases.map(item => ({ ...item, when: fn(item.when) })) } : fact;

export const renameFact = (config: ScenarioConfig, from: string, to: string): ScenarioConfig => {
  if (from === to) return config;
  const facts = config.facts.map(fact => ({
    ...mapCases(fact, when => renameFactInCondition(when, from, to)),
    name: fact.name === from ? to : fact.name,
    ...(fact.item_fields
      ? {
          item_fields: fact.item_fields.map(field =>
            field.allowed_values_from?.fact === from
              ? { ...field, allowed_values_from: { ...field.allowed_values_from, fact: to } }
              : field
          ),
        }
      : {}),
  }));
  return mapTools({ ...config, facts }, tool => {
    const requires = tool.requires.map(req => {
      if ('fact' in req && req.fact === from) return { ...req, fact: to };
      if (req.type === 'tool_success') {
        return { ...req, unchanged: req.unchanged.map(name => (name === from ? to : name)) };
      }
      if (req.type === 'condition') return { ...req, when: renameFactInCondition(req.when, from, to) };
      return req;
    });
    if (isSystemTool(tool)) {
      const bind = Object.fromEntries(
        Object.entries(tool.bind).map(([param, fact]) => [param, fact === from ? to : fact])
      );
      return { ...tool, requires, bind };
    }
    return {
      ...tool,
      requires,
      request: renameInRequest(tool.request, 'fact', from, to),
      response: {
        ...tool.response,
        save: tool.response.save.map(s => (s.fact === from ? { ...s, fact: to } : s)),
      },
      ...(tool.confirmation
        ? {
            confirmation: {
              ...tool.confirmation,
              review_fields: tool.confirmation.review_fields.map(name => (name === from ? to : name)),
            },
          }
        : {}),
    };
  });
};

export const renameTool = (config: ScenarioConfig, from: string, to: string): ScenarioConfig => {
  if (from === to) return config;
  const facts = config.facts.map(fact => mapCases(fact, when => renameToolInCondition(when, from, to)));
  return mapTools({ ...config, facts }, tool => ({
    ...tool,
    id: tool.id === from ? to : tool.id,
    requires: tool.requires.map(req => {
      if (req.type === 'tool_success' && req.tool === from) return { ...req, tool: to };
      if (req.type === 'condition') return { ...req, when: renameToolInCondition(req.when, from, to) };
      return req;
    }),
  }));
};

export const renameConnection = (config: ScenarioConfig, from: string, to: string): ScenarioConfig => {
  if (from === to) return config;
  return mapTools(
    {
      ...config,
      connections: config.connections.map(c => (c.id === from ? { ...c, id: to } : c)),
    },
    tool =>
      !isSystemTool(tool) && tool.request.connection === from
        ? { ...tool, request: { ...tool.request, connection: to } }
        : tool
  );
};

export const renameInput = (tool: HttpTool, from: string, to: string): HttpTool => {
  if (from === to) return tool;
  const rule = tool.response.success_rule;
  return {
    ...tool,
    inputs: tool.inputs.map(input => (input.name === from ? { ...input, name: to } : input)),
    request: renameInRequest(tool.request, 'input', from, to),
    ...(rule.type === 'exactly_one' && rule.narrow_by?.input === from
      ? { response: { ...tool.response, success_rule: { ...rule, narrow_by: { ...rule.narrow_by, input: to } } } }
      : {}),
  };
};

/** References follow a rename only when the old and new names are unambiguous. */
const canCascade = (names: string[], index: number, to: string) => {
  const from = names[index];
  if (!from || !to) return false;
  return names.every((name, i) => i === index || (name !== from && name !== to));
};

/** Changes the type of the fact at `index`; source formats of saves into it are dropped unless it stays temporal. */
export const withFactTypeAt = (config: ScenarioConfig, index: number, type: FactType): ScenarioConfig => {
  const fact = config.facts[index];
  const changed = setIn(config, ['facts', index], withFactType(fact, type));
  if (isTemporalType(type)) return changed;
  return mapTools(changed, tool =>
    isSystemTool(tool)
      ? tool
      : {
          ...tool,
          response: {
            ...tool.response,
            save: tool.response.save.map(s => (s.fact === fact.name ? omitKeys(s, 'format') : s)),
          },
        }
  );
};

/** Changes the source of the fact at `index`; a derived fact is a string or boolean with at least one case. */
export const withFactSourceAt = (
  config: ScenarioConfig,
  index: number,
  source: ScenarioFact['source']
): ScenarioConfig => {
  const fact = config.facts[index];
  if (source !== 'derived') return setIn(config, ['facts', index], { ...omitKeys(fact, 'cases', 'default'), source });
  const type = DERIVED_TYPES.includes(fact.type) ? fact.type : 'string';
  const typed = type === fact.type ? config : withFactTypeAt(config, index, type);
  const current = typed.facts[index];
  return setIn(typed, ['facts', index], {
    ...current,
    source,
    cases: current.cases?.length ? current.cases : [newDerivedCase(type)],
  });
};

export const renameFactAt = (config: ScenarioConfig, index: number, to: string): ScenarioConfig => {
  const names = config.facts.map(f => f.name);
  return canCascade(names, index, to)
    ? renameFact(config, names[index], to)
    : setIn(config, ['facts', index, 'name'], to);
};

export const renameToolAt = (config: ScenarioConfig, index: number, to: string): ScenarioConfig => {
  const names = config.tools.map(t => t.id);
  return canCascade(names, index, to)
    ? renameTool(config, names[index], to)
    : setIn(config, ['tools', index, 'id'], to);
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Re-applies a system tool template to the tool at `index`, keeping `enabled` and `requires`.
 * The id is renamed (with reference cascade) only when it was auto-generated from the previous template.
 */
export const switchSystemTemplate = (
  config: ScenarioConfig,
  index: number,
  meta: SystemToolMeta
): ScenarioConfig => {
  const tool = config.tools[index] as SystemTool;
  const template = newSystemTool(config, meta);
  const autoId = new RegExp(`^${escapeRegExp(tool.system)}(_\\d+)?$`).test(tool.id);
  const otherIds = config.tools.filter((_, i) => i !== index).map(t => t.id);
  const renamed = autoId ? renameToolAt(config, index, uniqueName(meta.id, otherIds)) : config;
  const current = renamed.tools[index] as SystemTool;
  return setIn(renamed, ['tools', index], {
    ...current,
    title: template.title,
    description: template.description,
    system: template.system,
    bind: template.bind,
    apply_corrections: template.apply_corrections,
    success_when: template.success_when,
  });
};

export const renameConnectionAt = (config: ScenarioConfig, index: number, to: string): ScenarioConfig => {
  const names = config.connections.map(c => c.id);
  return canCascade(names, index, to)
    ? renameConnection(config, names[index], to)
    : setIn(config, ['connections', index, 'id'], to);
};

export const renameInputAt = (tool: HttpTool, index: number, to: string): HttpTool => {
  const names = tool.inputs.map(i => i.name);
  return canCascade(names, index, to)
    ? renameInput(tool, names[index], to)
    : setIn(tool, ['inputs', index, 'name'], to);
};

export const issuesAt = (issues: ScenarioIssue[], pointer: string): ScenarioIssue[] =>
  issues.filter(issue => issue.pointer === pointer);

export const countIssuesUnder = (issues: ScenarioIssue[], pointer: string): number =>
  issues.filter(issue => issue.pointer === pointer || issue.pointer.startsWith(`${pointer}/`)).length;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const allObjects = (value: unknown) => Array.isArray(value) && value.every(isObject);

/** Returns an error when the document is too malformed to render in the form editor. */
export const formIncompatibility = (value: unknown): string | null => {
  if (!isObject(value)) return 'root';
  for (const key of ['connections', 'facts', 'tools']) {
    if (!allObjects(value[key])) return key;
  }
  for (const [index, fact] of (value.facts as Record<string, unknown>[]).entries()) {
    if (fact.item_fields !== undefined && !allObjects(fact.item_fields)) return `facts/${index}/item_fields`;
    if (fact.cases === undefined) continue;
    if (!allObjects(fact.cases) || (fact.cases as Record<string, unknown>[]).some(item => conditionIncompatible(item.when))) {
      return `facts/${index}/cases`;
    }
  }
  for (const [index, tool] of (value.tools as Record<string, unknown>[]).entries()) {
    const request = tool.request;
    const response = tool.response;
    if (!allObjects(tool.requires)) return `tools/${index}`;
    for (const requirement of tool.requires as Record<string, unknown>[]) {
      if (requirement.type === 'fact_in' && !Array.isArray(requirement.values)) return `tools/${index}/requires`;
      if (requirement.type === 'tool_success' && !Array.isArray(requirement.unchanged)) {
        return `tools/${index}/requires`;
      }
      if (requirement.type === 'condition' && conditionIncompatible(requirement.when)) return `tools/${index}/requires`;
    }
    if (tool.type === 'system') {
      if (!isObject(tool.bind)) return `tools/${index}/bind`;
      continue;
    }
    if (!allObjects(tool.inputs)) return `tools/${index}`;
    if (!isObject(request) || !isObject(response)) return `tools/${index}`;
    for (const key of ['path_params', 'query', 'body']) {
      if (!allObjects(request[key])) return `tools/${index}/request/${key}`;
      if ((request[key] as Record<string, unknown>[]).some(item => !isObject(item.value))) {
        return `tools/${index}/request/${key}`;
      }
    }
    if (!Array.isArray(response.success_status) || !isObject(response.success_rule) || !allObjects(response.save)) {
      return `tools/${index}/response`;
    }
    const narrowBy = response.success_rule.narrow_by;
    if (narrowBy !== undefined && (!isObject(narrowBy) || !Array.isArray(narrowBy.fields))) {
      return `tools/${index}/response`;
    }
    if (tool.confirmation !== undefined) {
      const confirmation = tool.confirmation;
      if (!isObject(confirmation) || !Array.isArray(confirmation.review_fields)) {
        return `tools/${index}/confirmation`;
      }
    }
  }
  return null;
};
